import BopisAPI from './BopisAPI';
import BopisStore from './BopisStore';

export default class BopisController {

	constructor () {

		/**
		 * Array of allowed stores
		 *
		 * @var {Array}
		 */
		this.allowedStores = window.mivaJS?.allowedStores ? window.mivaJS.allowedStores.split(',') : [];

		/**
		 * Bopis API Instance
		 *
		 * @var	BopisAPI
		 */
		this.api = new BopisAPI(this.allowedStores);

		/**
		 * Bopis Store Instance
		 *
		 * @var	BopisStore
		 */
		this.store = new BopisStore();

		/**
		 * State Object
		 *
		 * @var		{Object}
		 * @prop	{String}	messages		Messages to display on the Drawer
		 * @prop	{String}	resultMessages	Messages from Results (ex. No results found!)
		 */
		this.state = {
			messages: null,
			resultMessages: null
		};

		/**
		 * Default "No Results" Message
		 *
		 * @var {String}
		 */
		this.defaultNoResultsMessage = 'No Results. Try Searching a Different Term.';

		this.init();
	}

	/**
	 * Initalization Function to check some pieces for the user.
	 */
	init () {
		this.checkStore();
		this.checkGeolocation();
	}

	/**
	 * Check if the user has already used search.
	 * If not, retrieve their current location IF the state is granted.
	 * If we do have access to their location, and no "My Store" is set, set the closest one.
	 * @return {Boolean}
	 */
	async checkGeolocation () {
		if (this.store?.state?.lastSearch?.lat && this.store?.state?.lastSearch?.lng) {
			return this.setDefaultMyStore();
		}

		const geolocationPermissions = await navigator.permissions.query({name: 'geolocation'});
		if (geolocationPermissions.state === 'granted') {
			return this.useCurrentLocation();
		}

		return false;
	}

	/**
	 * If the state has a store identifier, but no information for the store, retrieve it.
	 * If the state has a store identifier that no longer exists, remove it from the state.
	 */
	async checkStore () {
		if (!this.store?.state?.myStore?.identifier || this.store?.state?.myStore?.name) {
			return;
		}

		const location = await this.api.singleLocationLookup(this.store.state.myStore.identifier);

		if (location === false) {
			this.store.set('myStore', {});
			return;
		}

		this.store.set('myStore', location);
	}

	/**
	 * Set the Store to "My Store"
	 * @param	Object	store
	 */
	setMyStore (store = {}) {
		this.store.set('myStore', store);
	}

	/**
	 * Ask the user to use their location. User will interact with a trigger to execute this.
	 */
	async useCurrentLocation () {
		const getLocation = await new Promise((resolve, reject) => {
			navigator.geolocation.getCurrentPosition(resolve, reject);
		}).then((position) => {
			return this.getLocationGeo(position);
		}).catch((error) => {
			return this.handleGeolocationError(error);
		});

		return getLocation;
	}

	/**
	 * Utilizes Geocode API to determine a user's location based on their lat/lng.
	 * Will attempt to set a default Store if one is not set yet.
	 */
	async getLocationGeo (position) {
		this.resetMessages();

		if (!position.coords) {
			this.updateMessage('Location is unavailable');
			return false;
		}

		if (position.coords.latitude !== this.store.state.lastSearch.lat || position.coords.longitude !== this.store.state.lastSearch.lng) {
			const location = await this.api.coordinatesLookup(position.coords.latitude, position.coords.longitude);

			if (!location) {
				this.updateMessage('Location is unavailable');
				return false;
			}

			this.store.set('lastSearch', {
				formattedTerm: this.formatGeolocationTerm(location),
				lat: position.coords.latitude,
				lng: position.coords.longitude,
				term: this.formatGeolocationTerm(location)
			});
		}

		this.setDefaultMyStore();

		return true;
	}

	/**
	 * Handles the Geolocation Error (displays to the user)
	 * @param	Object	error
	 */
	handleGeolocationError (error = {}) {
		let message = error.message;

		if (error.code === 1) {
			message = 'Please Enable Location Permissions';
		} else if (error.code === 2 || error.code === 3) {
			message = 'Location is unavailable';
		}

		this.updateMessage(message);

		return false;
	}

	/**
	 * Sets the messages to an empty string
	 * @param	Object	error
	 */
	resetMessages () {
		this.updateMessage('');
	}

	defaultResultData () {
		return {
			locations: [],
			message: '',
			showRadius: false
		};
	}

	/**
	 * Sets the messages to a string. Notifies Subscribers after.
	 * @param	String	message
	 */
	updateMessage (message) {
		this.state.messages = message;
		this.store.notifySubscribers();
	}

	/**
	 * Formats a geolocation's term (City, State)
	 * @param	{Object}	location
	 * @return	{String}
	 */
	formatGeolocationTerm (location) {
		const city = location.address_components.find((addressComponent) => addressComponent.types[0] === 'locality');
		const state = location.address_components.find((addressComponent) => addressComponent.types[0] === 'administrative_area_level_1');

		return `${city && city.short_name ? city.short_name + ', ' : ''}${state && state.short_name ? state.short_name : ''}`;
	}

	/**
	 * Formats a geolocation's term (City, State)
	 * @param	{String}	term	Search term from the user.
	 * @return	{Object}	location & message is returned
	 */
	async storeSearch (term) {
		let returnData = {...this.defaultResultData()};

		this.resetMessages();

		if (term && term !== this.store.state.lastSearch.term) {
			const location = await this.api.termLocationLookup(term);

			if (location === false) {
				this.updateMessage('Location is unavailable');
				return returnData;
			}

			this.store.set('lastSearch', {
				formattedTerm: this.formatGeolocationTerm(location),
				lat: location.geometry.location.lat,
				lng: location.geometry.location.lng,
				term: term
			});
		}

		const storeResults = await this.api.storeSearch(this.store.state.lastSearch.lat || 0, this.store.state.lastSearch.lng || 0);

		if (storeResults === false || storeResults?.length === 0) {
			return {
				locations: [],
				message: this.defaultNoResultsMessage,
				showRadius: false
			};
		}

		this.updateSavedStores(storeResults);

		return {
			locations: storeResults,
			message: '',
			showRadius: this.store.state.lastSearch?.lat || this.store.state.lastSearch?.lng
		};
	}

	/**
	 * Formats a geolocation's term (City, State)
	 * @param	{String}	term			Search term from the user.
	 * @param	{Object}	inventoryFields	Object of Inventory Fields for the Product
	 * @param   {Number}	quantity - Quantity to check against. Default: 1.
	 * @return	{Object}	location & message is returned
	 */
	async productStoreSearch (term, inventoryFields = {}, quantity = 1) {
		let returnData = {...this.defaultResultData()};

		this.resetMessages();

		if (term && term !== this.store.state.lastSearch.term) {
			const location = await this.api.termLocationLookup(term);

			if (location === false) {
				this.updateMessage('Location is unavailable');
				return returnData;
			}

			this.store.set('lastSearch', {
				formattedTerm: this.formatGeolocationTerm(location),
				lat: location.geometry.location.lat,
				lng: location.geometry.location.lng,
				term: term
			});
		}

		const storeResults = await this.api.productStoreSearch(this.store.state.lastSearch.lat || 0, this.store.state.lastSearch.lng || 0, inventoryFields, quantity);

		if (storeResults === false || storeResults?.length === 0) {
			return {
				locations: [],
				message: this.defaultNoResultsMessage,
				showRadius: false
			};
		}

		return {
			locations: storeResults,
			message: '',
			showRadius: this.store.state.lastSearch?.lat || this.store.state.lastSearch?.lng
		};
	}

	/**
	 * Checks to see if we can find a store for the user. It will set the closest store available (regardless of the default radius)
	 */
	async setDefaultMyStore () {
		if (this.store?.state?.myStore?.identifier) {
			return;
		}

		if (!this.store?.state?.lastSearch?.lat || !this.store?.state?.lastSearch?.lng) {
			return;
		}

		const store = await this.api.storeSearchNoRadius(this.store.state.lastSearch.lat, this.store.state.lastSearch.lng);

		if (store === false) {
			return;
		}

		this.setMyStore(store);
	}

	/**
	 * Updates `myStore`, in case the user wasn't sharing location previously.
	 */
	updateSavedStores (results) {
		if (this.store?.state?.myStore?.identifier) {
			const myStore = results.find((result) => result.identifier?.toString() === this.store.state.myStore.identifier?.toString());
			if (myStore) {
				this.setMyStore(myStore);
			}
		}
	}
}
